import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCustomDeviceSegment } from 'commons/src/actions/DeviceActions';
import { setCustomPeriod } from 'commons/src/commonFunctions';
import { DayUsageHours } from 'commons/src/models/commonTypeScript';
import { IaqInsightData } from '../../../models/common';
import { Store } from '../../../reducers';
import InsightTableRow from './InsightTable/InsightTableRow';

export type ParentProps = {
    sensorReport: IaqInsightData;
    sensor: string;
    usageHours: { [day: string]: DayUsageHours };
    from: Moment | null;
    to: Moment | null;
    timeZone: string;
};

export type DeviceReport = {
    name: string;
    serialNumber: string;
    average?: number;
    highestMeasurement?: number;
    lowestMeasurement?: number;
    roomType: string | undefined;
};

export const ResultTableComponent = ({
    usageHours,
    from,
    to,
    sensorReport,
    sensor,
    timeZone,
}: ParentProps): React.ReactElement => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();

    const devices = useSelector((state: Store) => state.devices.devicesWithKeyInfo);
    const segmentProperties = useSelector((state: Store) => state.segmentPropertiesStore.segmentProperties);
    const thresholds = useSelector((state: Store) => state.iaqInsight.thresholds);
    const [sneakPeakSensors, setSneakPeakSensor] = useState<string[]>([]);
    const sensorRanges = thresholds[sensor];
    const headers = ['AverageValue', 'InsightIaq.LowestMeasurement', 'InsightIaq.HighestMeasurement'];
    const columns = Object.keys(sensorReport.devicesOpeningHoursValues)
        .map(serialNumber => {
            const device = devices[serialNumber];
            const segmentProps = segmentProperties[serialNumber];
            return {
                ...sensorReport.devicesOpeningHoursValues[serialNumber],
                name: device.segmentName,
                serialNumber,
                roomType: segmentProps && segmentProps.roomType,
            };
        })
        .sort((deviceA, deviceB) => deviceA.name.localeCompare(deviceB.name));

    const clickSneakPeak = (serialNumber: string): void => {
        const alreadyOpen = sneakPeakSensors.includes(serialNumber);
        if (alreadyOpen) {
            setSneakPeakSensor(sneakPeakSensors.filter(serial => serial !== serialNumber));
        } else if (from) {
            const customPeriod = setCustomPeriod(
                moment(from.clone().subtract(1, 'days')),
                moment(to && to.clone().add(1, 'days'))
            );
            dispatch(fetchCustomDeviceSegment(serialNumber, customPeriod));
            setSneakPeakSensor([...sneakPeakSensors, serialNumber]);
        }
    };

    return (
        <div className="insight-tile__content insight-tile__content--table">
            <div className="insight-table">
                <div className="insight-table__row insight-table__row--header">
                    <div className="insight-table__cell insight-table__cell-header">
                        {txt('InsightIaq.DetailsWithinOpeningHours')}
                    </div>
                    {headers.map(headerText => (
                        <div className="insight-table__cell" key={`table-header-${headerText}`}>
                            {txt(headerText)}
                        </div>
                    ))}
                </div>
                {columns.map(device => (
                    <InsightTableRow
                        key={`table-element-${device.serialNumber}-${sensor}`}
                        deviceReport={device}
                        clickSneakPeak={clickSneakPeak}
                        showSneakPeak={sneakPeakSensors.includes(device.serialNumber)}
                        usageHours={usageHours}
                        sensorRanges={sensorRanges}
                        from={from}
                        to={to}
                        sensor={sensor}
                        sensorReports={sensorReport}
                        timeZone={timeZone}
                    />
                ))}
            </div>
        </div>
    );
};

export default ResultTableComponent;
